import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { API_BASE, SOCKET_URL, SOCKET_PATH } from '../config/api';

const CACHE_KEY = 'shop_order_status_cache';

export const DEFAULT_ORDER_STATUS = {
    orderEnabled: true,
    closedTitle: 'Tiệm Tạm Dừng Nhận Đơn Mới',
    closedReason: 'Để chuẩn bị chu đáo và đảm bảo chất lượng từng mẻ bánh tươi ngon nhất, tiệm xin phép tạm ngưng nhận thêm đơn đặt hàng vào lúc này. Rất mong quý khách thông cảm!',
    reopenTime: 'Dự kiến mở lại nhận đơn sớm nhất. Quý khách có thể xem trước menu hoặc liên hệ hotline để được hỗ trợ.',
    hotline: '0856.345.789',
    zaloLink: 'https://zalo.me/0856345789',
    updatedAt: new Date().toISOString()
};

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            return {
                ...DEFAULT_ORDER_STATUS,
                ...parsed,
                orderEnabled: parsed.orderEnabled !== undefined ? Boolean(parsed.orderEnabled) : true
            };
        }
    } catch (e) {
        // ignore JSON errors
    }
    return DEFAULT_ORDER_STATUS;
}

function writeCache(status) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(status));
    } catch (e) {}
    window.dispatchEvent(new CustomEvent('orderStatusUpdated', { detail: status }));
}

/**
 * Custom hook providing reactive order status across the whole application.
 * Realtime synchronization via Socket.IO.
 */
export default function useOrderStatus() {
    const [orderStatus, setOrderStatus] = useState(readCache);
    const [loading, setLoading] = useState(true);

    const refetch = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE}/bill/order-status`, { timeout: 6000 });
            const data = res.data?.data || res.data;
            if (data && typeof data === 'object') {
                const merged = {
                    ...DEFAULT_ORDER_STATUS,
                    ...data,
                    orderEnabled: data.orderEnabled !== undefined ? Boolean(data.orderEnabled) : true
                };
                setOrderStatus(merged);
                writeCache(merged);
            }
        } catch (err) {
            console.warn('Lỗi tải trạng thái nhận đơn từ máy chủ:', err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refetch();

        // Listen for internal window events (cross-component sync)
        const onInternalSync = (e) => {
            if (e.detail) setOrderStatus(e.detail);
            else setOrderStatus(readCache());
        };
        window.addEventListener('orderStatusUpdated', onInternalSync);
        window.addEventListener('storage', onInternalSync);

        // Socket.IO realtime connection
        let socket;
        try {
            socket = io(SOCKET_URL, {
                path: SOCKET_PATH,
                transports: ['websocket', 'polling'],
                withCredentials: true
            });

            socket.on('orderStatusChanged', (newData) => {
                if (newData && typeof newData === 'object') {
                    const merged = {
                        ...DEFAULT_ORDER_STATUS,
                        ...newData,
                        orderEnabled: newData.orderEnabled !== undefined ? Boolean(newData.orderEnabled) : true
                    };
                    setOrderStatus(merged);
                    writeCache(merged);
                }
            });
        } catch (sockErr) {
            console.warn('Socket connection error in useOrderStatus:', sockErr.message);
        }

        return () => {
            window.removeEventListener('orderStatusUpdated', onInternalSync);
            window.removeEventListener('storage', onInternalSync);
            if (socket) {
                socket.off('orderStatusChanged');
                socket.disconnect();
            }
        };
    }, [refetch]);

    const updateOrderStatus = useCallback(async (newSettings) => {
        try {
            const res = await axios.post(`${API_BASE}/bill/order-status`, newSettings);
            const saved = res.data?.data || res.data;
            const merged = {
                ...orderStatus,
                ...saved,
                orderEnabled: saved.orderEnabled !== undefined ? Boolean(saved.orderEnabled) : orderStatus.orderEnabled
            };
            setOrderStatus(merged);
            writeCache(merged);
            return { success: true, data: merged };
        } catch (err) {
            console.error('Lỗi cập nhật trạng thái nhận đơn:', err);
            return { success: false, error: err.response?.data?.message || err.message };
        }
    }, [orderStatus]);

    const toggleOrderStatus = useCallback(async (overrideValue) => {
        const nextState = overrideValue !== undefined ? Boolean(overrideValue) : !orderStatus.orderEnabled;
        return await updateOrderStatus({
            ...orderStatus,
            orderEnabled: nextState
        });
    }, [orderStatus, updateOrderStatus]);

    return {
        orderStatus,
        orderEnabled: orderStatus.orderEnabled,
        loading,
        refetch,
        updateOrderStatus,
        toggleOrderStatus
    };
}
